/**
 * ReportProblemTemplate
 *
 * Pure presentation template for the "Report a Problem" page.
 * All business logic (state, submission, navigation) is handled by the wrapper.
 *
 * When the optional `layout` prop is provided, the template wraps its content
 * in `AppLayout` so it renders alongside the shared sidebar (same pattern as
 * `MyProfileTemplate` and `BillingPageTemplate`). When omitted, the form
 * renders standalone (used in plugin embedding).
 *
 * @module @ideoz/ui/components/templates/ReportProblemTemplate
 */

import React from 'react';
import { Button } from '../atoms/button';
import { ReportProblemTemplateProps } from '../../types/ReportProblem.types';
import { Label } from '../atoms/label';
import { Textarea } from '../atoms/textarea';
import { Card } from '../atoms/card';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../atoms/select';
import { AppLayout } from '../layouts/AppLayout';

/**
 * ReportProblemTemplate Component
 *
 * Renders the Report a Problem form matching the Figma design.
 * Pure presentation component - no Redux, no API, no navigation.
 */
export default function ReportProblemTemplate(props: ReportProblemTemplateProps) {
  const {
    issueType,
    onIssueTypeChange,
    description,
    onDescriptionChange,
    error,
    isSubmitting,
    onSubmit,
    onCancel,
    maxChars = 2000,
    layout,
  } = props;

  const formContent = (
    <div className="min-h-screen flex items-start justify-center pt-16 px-6 bg-transparent">
      <div className="w-full max-w-[700px] flex flex-col gap-[18px]">
        <h1
          className="text-2xl font-semibold leading-8 text-[#252b37]"
          data-testid="report-problem-title"
        >
          Report a Problem
        </h1>

        <Card className="w-full rounded-2xl border-0 shadow-none bg-white px-6 pt-6 pb-0.5 flex flex-col gap-6">
          <div className="flex flex-col gap-1">
            <Label
              htmlFor="report-issue-type"
              className="text-sm font-medium text-[#0a0a0a] leading-5"
            >
              What&apos;s the issue?
            </Label>
            <Select
              value={issueType ?? ''}
              onValueChange={(v) => onIssueTypeChange(v ? (v as any) : null)}
            >
              <SelectTrigger
                id="report-issue-type"
                aria-invalid={!!error}
                aria-describedby={error ? 'report-issue-error' : undefined}
                className="w-full rounded-lg border-[#d4d4d4] min-h-9 px-3 text-sm shadow-xs"
                size="default"
              >
                <SelectValue placeholder="Select the issue you faced" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bug">Bug / Something not working</SelectItem>
                <SelectItem value="wrong_result">Wrong result</SelectItem>
                <SelectItem value="performance">Performance issue</SelectItem>
                <SelectItem value="ui">UI issue</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
            {error ? (
              <p id="report-issue-error" className="text-red-600 text-xs mt-1">
                {error}
              </p>
            ) : null}
          </div>

          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <Label
                htmlFor="report-description"
                className="text-sm font-medium text-[#0a0a0a] leading-5"
              >
                Describe the problem
              </Label>
              <span className="text-xs text-[#737373] leading-4">(Optional)</span>
            </div>
            <Textarea
              id="report-description"
              value={description ?? ''}
              onChange={(e) => onDescriptionChange(e.target.value)}
              placeholder="Explain the problem in detail"
              className="h-[76px] rounded-lg border-[#e5e5e5] text-sm placeholder:text-[#737373] shadow-xs resize-none"
              maxLength={maxChars}
            />
          </div>

          <div className="flex items-center justify-end gap-2 p-4">
            <Button
              variant="secondary-gray"
              size="sm"
              className="min-h-9 rounded-full px-4"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              size="sm"
              className="min-h-9 rounded-full px-4"
              onClick={onSubmit}
              disabled={isSubmitting}
              aria-busy={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send'}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );

  if (!layout) {
    return formContent;
  }

  return (
    <AppLayout
      currentPath={layout.currentPath ?? '/report-problem'}
      sidebar={layout.sidebar}
      user={layout.user}
      userMenuProps={layout.userMenuProps}
      isWeb={layout.isWeb}
      testId={layout.testId ?? 'report-problem-sidemenu'}
    >
      {formContent}
    </AppLayout>
  );
}
